import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const MOROCCAN_LEAVE_TYPES = [
  { id: 'conge_annuel', label: 'Congé Annuel (Paid Leave)' },
  { id: 'maladie', label: 'Congé de Maladie (Sick Leave)' },
  { id: 'maternite', label: 'Congé de Maternité (14 Weeks)' },
  { id: 'paternite', label: 'Congé de Paternité (3 Days)' },
  { id: 'mariage', label: 'Mariage (4 Days)' },
  { id: 'deces', label: 'Décès d\'un proche (3 Days)' }
];

export default function LeaveModal({ 
  showModal, 
  setShowModal, 
  newLeave, 
  setNewLeave, 
  handleCreateLeave 
}) {
  const { tenantId } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    async function fetchLeaveTypes() {
      try {
        const res = await api.get(`/tenants/${tenantId}/holidays/leave-types`);
        setLeaveTypes(res.data.data || []);
        if (res.data.data.length > 0 && !newLeave.type) {
          setNewLeave(prev => ({ ...prev, type: res.data.data[0].id }));
        }
      } catch (err) {
        console.error('Failed to fetch leave types', err);
      }
    }
    if (showModal && leaveTypes.length === 0) {
      fetchLeaveTypes();
    }
  }, [showModal, tenantId, leaveTypes.length, setNewLeave, newLeave.type]);

  useEffect(() => {
    if (showModal && newLeave.startDate && newLeave.endDate && newLeave.type) {
      const fetchPreview = async () => {
        setLoadingPreview(true);
        setPreviewError(null);
        try {
          const res = await api.post(`/tenants/${tenantId}/holidays/preview`, {
            startDate: newLeave.startDate,
            endDate: newLeave.endDate,
            type: newLeave.type,
            isHalfDay: newLeave.isHalfDay
          });
          setPreview(res.data.data);
        } catch (err) {
          setPreview(null);
          setPreviewError(err.response?.data?.error?.message || 'Error calculating days');
        } finally {
          setLoadingPreview(false);
        }
      };
      
      const start = new Date(newLeave.startDate);
      const end = new Date(newLeave.endDate);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
        fetchPreview();
      } else {
        setPreview(null);
        setPreviewError(null);
      }
    } else {
      setPreview(null);
      setPreviewError(null);
    }
  }, [showModal, newLeave.startDate, newLeave.endDate, newLeave.type, tenantId]);

  if (!showModal) return null;

  const selectedTypeObj = leaveTypes.find(t => t.id === newLeave.type);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem' }}>
            <Calendar size={20} color="var(--primary-500)" />
            Request Time Off
          </h2>
          <button 
            onClick={() => setShowModal(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleCreateLeave}>
          <div className="form-group">
            <label className="form-label">Type of Leave</label>
            <select 
              className="form-input" 
              value={newLeave.type || ''}
              onChange={(e) => setNewLeave({...newLeave, type: e.target.value})}
              required
            >
              {leaveTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Title / Description</label>
            <input 
              type="text" 
              className="form-input" 
              placeholder="e.g. Summer Vacation, Medical Appt..."
              value={newLeave.title}
              onChange={(e) => setNewLeave({...newLeave, title: e.target.value})}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input 
                type="date" 
                className="form-input" 
                value={newLeave.startDate}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewLeave(prev => ({
                    ...prev, 
                    startDate: val,
                    endDate: prev.isHalfDay ? val : prev.endDate
                  }));
                }}
                required
              />
            </div>
            {!newLeave.isHalfDay && (
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={newLeave.endDate}
                  onChange={(e) => setNewLeave({...newLeave, endDate: e.target.value})}
                  required
                />
              </div>
            )}
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input 
              type="checkbox" 
              id="halfDayCheck"
              checked={newLeave.isHalfDay || false}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setNewLeave(prev => ({
                  ...prev, 
                  isHalfDay: isChecked,
                  endDate: isChecked ? prev.startDate : prev.endDate
                }));
              }}
            />
            <label htmlFor="halfDayCheck" className="form-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
              Request Half-Day (Morning or Afternoon)
            </label>
          </div>

          {selectedTypeObj?.requiresAttachment && (
            <div className="form-group">
              <label className="form-label" style={{ color: 'var(--accent-amber)' }}>File Attachment Required (e.g. Medical Certificate)</label>
              <input 
                type="file" 
                className="form-input" 
                onChange={(e) => setNewLeave({...newLeave, file: e.target.files[0]})}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea 
              className="form-input" 
              rows="3"
              placeholder="Optional notes for HR or your Manager..."
              value={newLeave.notes || ''}
              onChange={(e) => setNewLeave({...newLeave, notes: e.target.value})}
            />
          </div>

          {/* Live Preview Box */}
          <div style={{ marginTop: '16px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', minHeight: '80px' }}>
            {loadingPreview ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Calculating days...</div>
            ) : previewError ? (
              <div style={{ display: 'flex', gap: '8px', color: 'var(--accent-rose)', fontSize: '0.9rem' }}>
                <AlertCircle size={16} />
                {previewError}
              </div>
            ) : preview ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 500 }}>Billable Days:</span>
                  <span style={{ fontWeight: 600, color: 'var(--primary-600)' }}>{preview.billableDays}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  <span>Calendar Days:</span>
                  <span>{preview.calendarDays}</span>
                </div>
                {preview.excluded?.length > 0 && (
                  <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--accent-amber)' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Excluded Days:</div>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {preview.excluded.map((ex, i) => (
                        <li key={i}>{ex.date}: {ex.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Select dates to see deduction preview.</div>
            )}
          </div>

          <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button 
              type="button" 
              className="btn btn-outline" 
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!!previewError || loadingPreview}>
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
